import { Link } from "react-router-dom";
import { assetUrl } from "@/utils/assetUrl";

/**
 * 解决方案各分类面板（通用 CategoryPanel / AI 业务专属面板）共用的
 * 子方案卡片网格与关联案例行。从 SolutionsBlocks 抽出，避免多处复制。
 */

// ─── Sub-scene card grid ─────────────────────────────────────────────────────

export interface SubScene {
  id: string;
  icon: string;
  name: string;
  desc: string;
  tags: string[];
  productCategory: string;
  image: string;
}

export interface SubSceneRowProps {
  scenes: SubScene[];
  accentColor: string;
  onNavigate: (category: string) => void;
}

interface SubSceneCardProps {
  scene: SubScene;
  accentColor: string;
  onNavigate: (category: string) => void;
}

function SubSceneCard({ scene, accentColor, onNavigate }: SubSceneCardProps) {
  return (
    <div
      onClick={() => onNavigate(scene.id)}
      className="group h-full rounded-2xl overflow-hidden border border-white/60 hover:border-white transition-all duration-300 cursor-pointer bg-white/80 backdrop-blur-sm flex flex-col"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
    >
      <div className="relative w-full h-44 overflow-hidden flex-shrink-0">
        <img
          src={assetUrl(scene.image)}
          alt={scene.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/75 to-transparent"></div>
        <div className="absolute bottom-3 left-4 flex items-center gap-2">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-lg"
            style={{ backgroundColor: `${accentColor}cc` }}
          >
            <i className={`${scene.icon || "ri-shield-line"} text-white text-sm`}></i>
          </div>
          <span className="text-white text-sm font-bold">{scene.name}</span>
        </div>
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-full"
            style={{ backgroundColor: `${accentColor}dd` }}
          >
            <i className="ri-arrow-right-up-line text-white text-sm"></i>
          </div>
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{scene.desc}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto mb-3 content-start">
          {scene.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 text-xs rounded-full border h-fit"
              style={{
                backgroundColor: `${accentColor}08`,
                color: accentColor,
                borderColor: `${accentColor}25`,
              }}
            >
              {tag}
            </span>
          ))}
        </div>
        <div
          className="flex items-center gap-1.5 text-xs font-semibold group-hover:gap-2.5 transition-all duration-200"
          style={{ color: accentColor }}
        >
          <span>查看方案详情</span>
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs"></i>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SubSceneGridRow({ scenes, accentColor, onNavigate }: SubSceneRowProps) {
  return (
    <div className="flex flex-wrap justify-center gap-5 mb-8">
      {scenes.map((scene) => (
        <div key={scene.id} className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-13.33px)] xl:w-[calc(25%-15px)]">
          <SubSceneCard scene={scene} accentColor={accentColor} onNavigate={onNavigate} />
        </div>
      ))}
    </div>
  );
}

// ─── Related cases row (from API) ────────────────────────────────────────────

export interface DynamicCasesRowProps {
  cases: any[];
  accentColor: string;
  categoryId: string;
}

export function DynamicCasesRow({ cases, accentColor, categoryId }: DynamicCasesRowProps) {
  if (cases.length === 0) return null;
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-5 rounded-full" style={{ backgroundColor: accentColor }}></div>
          <h4 className="text-base font-bold text-[#0d1f3c]">行业标杆案例</h4>
        </div>
        <Link
          to={`/cases?solution=${categoryId}`}
          className="flex items-center gap-1.5 text-sm font-medium hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer"
          style={{ color: accentColor }}
        >
          查看全部案例
          <i className="ri-arrow-right-line text-sm"></i>
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cases.map((c: any) => (
          <Link
            key={c.id}
            to={`/cases/${c.id}`}
            className="bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all duration-300 group cursor-pointer block"
          >
            <div className="relative w-full h-40 overflow-hidden">
              <img src={assetUrl(c.image)} alt={c.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/60 to-transparent"></div>
              <div className="absolute top-3 left-3">
                <span className="px-2.5 py-1 text-white text-xs font-medium rounded-full" style={{ backgroundColor: `${accentColor}dd` }}>
                  {c.solutionName || c.industry || ""}
                </span>
              </div>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-400 mb-1.5">{c.client}</p>
              <h5 className="text-sm font-bold text-[#0d1f3c] mb-2.5 leading-snug line-clamp-2">{c.title}</h5>
              <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{c.summary ?? c.description ?? ""}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
